import { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { history } from '../../../store';
import ReturnButtons from '../../components/core/ReturnButtons';
import { makeStyles, ThemeProvider, useTheme } from '@material-ui/core/styles';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { loadScript } from '../../../services/dynamic-script';
import {
  FormControlLabel,
  RadioGroup,
  Box,
  Card,
  Grid,
  Grow,
  IconButton,
  Slider,
  Button,
  Paper,
  Typography,
  Radio,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Tooltip,
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useTranslation } from 'react-i18next';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import SimpleInfoDialog from '../../components/dialog/SimpleInfoDialog';
import { Actions as EUSCoast } from '../../../store/ducks/euscoast';
import { landCoversIntersectionsTurf, extractShoreLineTurf, classifyUrbanGrowth } from '../../../algorithms/euscoast';
import { exportGeoJson } from '../../../algorithms/euscoast/geoJsonExport';
import { exportSession, parseCsv, useLocalStorage } from '../../../common/utils.js';
import * as turf from '@turf/turf';
import TransectResult from '../../components/euscoast/_TransectResult';
import Export from '../../../services/export';
import { periodFilenamePart, timer } from '../../../algorithms/utils';
import { GoogleMap } from '../../components';
import { Actions as Map } from '../../../store/ducks/map';
import AnalysisSteps from '../../components/steps/AnalysisSteps';
import { useRegisterGeoJsonExport, useRegisterPdfExport, useRegisterSessionExport, useThemeMode } from '../../contexts/index.jsx';
import {
  buildMapStyles,
  createShorelineTheme,
  OVERLAY_COLUMN_MAX_HEIGHT,
  OVERLAY_COLUMN_MAX_HEIGHT_SAFE,
  OVERLAY_SIDE_GUTTER,
  OVERLAY_SCROLLBAR_GUTTER,
  OVERLAY_COLUMN_PADDING_TOP,
  OVERLAY_SIDE_COLUMN_WIDTH,
  OVERLAY_MAIN_COLUMN_WIDTH,
  OVERLAY_COLUMN_LAYOUT,
  fitBoundsWithOverlayPadding,
  fitCoordinatesWithOverlayPadding,
} from '../Shoreline/theme.js';
import BackgroundMapPanel from '../../components/map/BackgroundMapPanel';
import OverlayPanelsToggle from '../../components/map/OverlayPanelsToggle';
import { TerraDrawManager } from '../../../common/TerraDrawManager';
import ShapeEditControl from '../../components/map/ShapeEditControl';
import { BASELINE_VERTEX_EDITING_ENABLED, useShapeVertexEditing } from '../../../hooks/useShapeVertexEditing';
import { buildSessionExport, readPendingBaseline } from '../../../algorithms/euscoast/sessionExport.js';
import { buildLandCoverReport, REPORT_MODULE } from '../../../algorithms/euscoast/pdfReport';
import { captureImageForReport, renderReport, reportFileName } from '../../../common/pdfReport';
import { APP_ROOT_SELECTOR, settleMapForCapture } from '../../../common/captureElement';
import { aoiPaths } from '../../../common/aoi';

/**
 * Land-cover translation keys, indexed by the class label the classification carries
 * (0 mangrove, 1 vegetation, 2 water, 3 human intervention, 4 beach/dune/sand spot).
 */
const LAND_COVER_KEYS = ['mangrove', 'vegetation', 'water', 'human', 'beach'];

const LAND_COVER_COLORS = ['#687537', '#129912', '#0000ff', '#af2a2a', '#e3c565'];

/**
 * @function toQgisColor
 * @description Converts a hex color into the "R,G,B,A" string a QGIS symbol layer expects.
 * @param {String} hex - Color as #rrggbb.
 * **Called by:
 * @see {buildLandCoverStyle}
 * @memberof module:Euscoast
 * @returns {String} The color as "R,G,B,255".
 */
const toQgisColor = (hex) => {
  const value = parseInt(hex.slice(1), 16);
  return `${(value >> 16) & 255},${(value >> 8) & 255},${value & 255},255`;
};

/**
 * @function buildLandCoverStyle
 * @description Builds the QGIS layer style (.qml) that paints the exported land-cover polygons by
 * their `class` field, with the same colors the classified raster uses. Shipped inside the zip
 * next to each shapefile, where QGIS picks it up on its own - without it the layer opens under a
 * single symbol, one flat color for every class.
 * @param {Array<Object>} categories - Classes present in the layer, as {value, label} entries.
 * @see {@link toQgisColor}
 * **Called by:
 * @see {handleVectorExport}
 * @memberof module:Euscoast
 * @returns {String} The .qml document.
 */
const buildLandCoverStyle = (categories) => {
  const category = ({ value, label }) =>
    `<category value="${value}" symbol="${value}" label="${label}" render="true" type="double"/>`;
  const symbol = ({ value }) =>
    `<symbol name="${value}" type="fill" alpha="1" clip_to_extent="1" force_rhr="0">` +
    `<layer class="SimpleFill" enabled="1" locked="0" pass="0">` +
    `<Option type="Map">` +
    `<Option name="color" type="QString" value="${toQgisColor(LAND_COVER_COLORS[value] || '#808080')}"/>` +
    `<Option name="outline_style" type="QString" value="no"/>` +
    `<Option name="style" type="QString" value="solid"/>` +
    `</Option>` +
    `</layer>` +
    `</symbol>`;
  return (
    `<!DOCTYPE qgis PUBLIC 'http://mrcc.com/qgis.dtd' 'SYSTEM'>\n` +
    `<qgis version="3.28.0" styleCategories="Symbology">\n` +
    `<renderer-v2 type="categorizedSymbol" attr="class" forceraster="0" symbollevels="0" enableorderby="0">\n` +
    `<categories>${categories.map(category).join('')}</categories>\n` +
    `<symbols>${categories.map(symbol).join('')}</symbols>\n` +
    `</renderer-v2>\n` +
    `</qgis>\n`
  );
};

/**
 * @function collectRings
 * @description Flattens a vectorized land-cover geometry into a flat list of rings. Earth Engine
 * hands back one dissolved geometry per class, and the way it groups the rings of that geometry
 * cannot be trusted to follow the GeoJSON convention (first ring is the shell, the rest are its
 * holes) - the parts of a class scattered over the AOI can all land in a single ring list. Every
 * ring is therefore collected on its own and re-grouped afterwards by `organizeRings`.
 * @param {Object} geometry - GeoJSON geometry to flatten.
 * @param {Array<Array>} [rings=[]] - Accumulator, used by the recursive GeometryCollection case.
 * @see {@link organizeRings}
 * **Called by:
 * @see {geometryToPolygons}
 * @memberof module:Euscoast
 * @returns {Array<Array>} Every ring found in the geometry.
 */
const collectRings = (geometry, rings = []) => {
  if (!geometry) return rings;
  switch (geometry.type) {
    case 'Polygon':
      geometry.coordinates.forEach((ring) => rings.push(ring));
      break;
    case 'MultiPolygon':
      geometry.coordinates.forEach((polygon) => polygon.forEach((ring) => rings.push(ring)));
      break;
    case 'GeometryCollection':
      (geometry.geometries || []).forEach((inner) => collectRings(inner, rings));
      break;
    default:
      break;
  }
  return rings;
};

/**
 * @function ringBounds
 * @description Bounding box of a ring, used to reject containment tests cheaply.
 * @param {Array<Array<Number>>} ring - Ring coordinates, as [lng, lat] pairs.
 * **Called by:
 * @see {organizeRings}
 * @memberof module:Euscoast
 * @returns {Array<Number>} [minX, minY, maxX, maxY].
 */
const ringBounds = (ring) =>
  ring.reduce(
    ([minX, minY, maxX, maxY], [x, y]) => [Math.min(minX, x), Math.min(minY, y), Math.max(maxX, x), Math.max(maxY, y)],
    [Infinity, Infinity, -Infinity, -Infinity],
  );

/**
 * @function ringArea
 * @description Shoelace area of a ring; only its magnitude is used, to sort rings from the
 * largest (outermost) to the smallest.
 * @param {Array<Array<Number>>} ring - Ring coordinates.
 * **Called by:
 * @see {organizeRings}
 * @memberof module:Euscoast
 * @returns {Number} The ring's absolute area, in squared degrees.
 */
const ringArea = (ring) => {
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % ring.length];
    sum += x1 * y2 - x2 * y1;
  }
  return Math.abs(sum / 2);
};

/**
 * @function pointInRing
 * @description Ray-casting test of whether a point falls inside a ring.
 * @param {Array<Number>} point - [lng, lat] to test.
 * @param {Array<Array<Number>>} ring - Ring coordinates.
 * **Called by:
 * @see {organizeRings}
 * @memberof module:Euscoast
 * @returns {Boolean} True when the point is inside the ring.
 */
const pointInRing = ([x, y], ring) => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};

/**
 * @function organizeRings
 * @description Rebuilds proper polygons out of a flat ring list, the way a GIS reader does: rings
 * are walked from the largest to the smallest, each one is nested under the smallest ring that
 * contains it, and its nesting depth decides its role - an evenly nested ring is a shell, an oddly
 * nested one is a hole of the shell around it (so an island inside a lagoon is a shell again).
 * Without this every ring after the first would be exported as a hole of the first one, which is
 * what makes a class fill its whole area of interest and hide the classes underneath it.
 * @param {Array<Array>} rings - Flat ring list, as returned by `collectRings`.
 * @see {@link pointInRing}
 * **Called by:
 * @see {geometryToPolygons}
 * @memberof module:Euscoast
 * @returns {Array<Array>} One entry per polygon: its shell followed by its holes.
 */
const organizeRings = (rings) => {
  const placed = [];
  rings
    .filter((ring) => ring && ring.length >= 4)
    .map((ring) => ({
      ring,
      bounds: ringBounds(ring),
      area: ringArea(ring),
      inner: [(ring[0][0] + ring[1][0]) / 2, (ring[0][1] + ring[1][1]) / 2],
      depth: 0,
      holes: [],
    }))
    .sort((a, b) => b.area - a.area)
    .forEach((entry) => {
      let parent = null;
      for (let i = placed.length - 1; i >= 0 && !parent; i--) {
        const candidate = placed[i];
        if (
          candidate.bounds[0] <= entry.bounds[0] &&
          candidate.bounds[1] <= entry.bounds[1] &&
          candidate.bounds[2] >= entry.bounds[2] &&
          candidate.bounds[3] >= entry.bounds[3] &&
          pointInRing(entry.inner, candidate.ring)
        ) {
          parent = candidate;
        }
      }
      if (parent) {
        entry.depth = parent.depth + 1;
        if (entry.depth % 2 === 1) parent.holes.push(entry.ring);
      }
      placed.push(entry);
    });
  return placed.filter((entry) => entry.depth % 2 === 0).map((entry) => [entry.ring, ...entry.holes]);
};

/**
 * @function geometryToPolygons
 * @description Turns one class's dissolved geometry into the individual polygons that make it up,
 * each with its own holes, so every one of them becomes a separate shapefile record.
 * @param {Object} geometry - GeoJSON geometry of a single land-cover class.
 * @see {@link collectRings}
 * @see {@link organizeRings}
 * **Called by:
 * @see {handleVectorExport}
 * @memberof module:Euscoast
 * @returns {Array<Array>} One entry per polygon: its shell followed by its holes.
 */
const geometryToPolygons = (geometry) => organizeRings(collectRings(geometry));

/**
 * @function getPeriodSuffix
 * @description Extracts the numeric part after the first dash of a period key (`2020-1` -> 1),
 * used to order multiple analyses that share the same base year. Falls back to -Infinity when the
 * key has no suffix.
 * @param {String|Number} key - Period key, typically a year like "2020" or "2020-1".
 * **Called by:
 * @see {comparePeriodKeys}
 * @memberof module:Euscoast
 * @returns {Number} The suffix, or -Infinity when there is none.
 */
const getPeriodSuffix = (key) => {
  const str = String(key);
  const suffixIndex = str.indexOf('-');
  if (suffixIndex === -1) return -Infinity;
  const suffix = parseInt(str.slice(suffixIndex + 1), 10);
  return Number.isFinite(suffix) ? suffix : -Infinity;
};

/**
 * @function comparePeriodKeys
 * @description Compares two period keys ("2020", "2020-1" ...) for the analysis's year ordering:
 * base year first, then the suffix descending, so the plain year of a pair sorts after its
 * suffixed half.
 * @param {String|Number} a - First period key.
 * @param {String|Number} b - Second period key.
 * @see {@link getPeriodSuffix}
 * **Called by:
 * @see {ProcessingPage} - sorting the analysis years when the acquisition used equal base years.
 * @memberof module:Euscoast
 * @returns {Number} Negative when `a` sorts before `b`, positive afterwards.
 */
const comparePeriodKeys = (a, b) => {
  const baseA = parseInt(String(a).slice(0, 4), 10);
  const baseB = parseInt(String(b).slice(0, 4), 10);
  if (baseA !== baseB) return baseA - baseB;
  return getPeriodSuffix(b) - getPeriodSuffix(a);
};

/**
 * @function ProcessingPage
 * @description EUSCoast processing page. Uses the shared AnalysisSteps component for the
 * baseline drawing and transect configuration workflow, then runs the per-transect land-cover
 * intersection analysis (coastal squeeze), with results exportable as CSV, shapefile and
 * vectorized land-cover polygons.
 * @see {@link AnalysisSteps} - manages the step flow (draw baseline, configure transects, analyze).
 * @see {@link generateTransects} - creates transects along the drawn baseline using turf.js.
 * @see {@link startCalc} - runs the per-transect land-cover intersection analysis.
 * @see {@link TransectResult} - renders each transect's result.
 * **Called by:
 * @see {MainPage} - via the `/euscoast/result` Route (imported as ProcessingEUSCoastPage).
 * @memberof module:Euscoast
 * @returns {JSX.Element} Complete processing page JSX element
 */
const ProcessingPage = () => {
  const dispatch = useDispatch();
  const [t, i18n] = useTranslation();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const classes = useStyles();
  const mapRef = useRef();
  const drawingManager = useRef();
  const ROI = useRef();
  const boundsRef = useRef();
  const transectMarker = useRef();
  const baselineRef = useRef();
  const baselineBufferRef = useRef([]);

  const outerTheme = useTheme();
  const { shorelineThemeMode } = useThemeMode();
  const euscoastTheme = useMemo(
    () => createShorelineTheme(shorelineThemeMode, outerTheme),
    [outerTheme, shorelineThemeMode],
  );
  const themeModeRef = useRef(shorelineThemeMode);
  const [mapType, setMapType] = useLocalStorage('euscoastMapType', 'roadmap');
  const [labelsVisible, setLabelsVisible] = useLocalStorage('euscoastMapLabels', true);
  const labelsVisibleRef = useRef(labelsVisible);

  /**
   * @function applyMapThemeStyle
   * @description Recomputes the live map's `styles` array from the module theme mode, the
   * map's current base layer and the "Marcadores" toggle. Dark styling applies only when the
   * mode is dark AND the base layer is roadmap/terrain — satellite/hybrid imagery is never
   * recolored. Reads both inputs through refs because this also runs from the map's own
   * `maptypeid_changed` listener, which is registered once and would otherwise close over the
   * values they had at mount.
   * @see {@link buildMapStyles}
   * **Called by:
   * @see {ProcessingPage} - on theme-mode / labels change, and via the map's maptypeid_changed listener.
   * @memberof module:EUSCoast
   * @returns {void}
   */
  const applyMapThemeStyle = () => {
    if (!mapRef.current) return;
    mapRef.current.setOptions({
      styles: buildMapStyles({
        themeMode: themeModeRef.current,
        mapType: mapRef.current.getMapTypeId(),
        labelsVisible: labelsVisibleRef.current,
      }),
    });
  };

  /**
   * @function handleMapTypeChange
   * @description Switches the live Google Maps instance's base layer from the compact "Mapa de
   * fundo" panel, without EUSCoast's old native map-type control (now disabled).
   * @param {String} type - A Google Maps `mapTypeId` (`'roadmap'`, `'satellite'`, `'hybrid'`, or
   * `'terrain'`).
   * **Called by:
   * @see {ProcessingPage} - via `BackgroundMapPanel`'s `onMapTypeChange`.
   * @memberof module:EUSCoast
   * @returns {void}
   */
  const handleMapTypeChange = (type) => {
    setMapType(type);
    mapRef.current?.setMapTypeId(type);
  };

  useEffect(() => {
    themeModeRef.current = shorelineThemeMode;
    labelsVisibleRef.current = labelsVisible;
    applyMapThemeStyle();
  }, [shorelineThemeMode, labelsVisible]);

  const [images] = useSelector((state) => state.euscoast.studyData);
  const classLandCoversVectors = useSelector((state) => state.euscoast.classLandCoversVectors);
  const classLandCoverPolygons = useSelector((state) => state.euscoast.classLandCoverPolygons);
  const waterLandCoverVectors = useSelector((state) => state.euscoast.waterLandCoverVectors);
  const euscoastYears = useSelector((state) => state.euscoast.years);
  const euscoastDates = useSelector((state) => state.euscoast.dates);
  const euscoastMissions = useSelector((state) => state.euscoast.missions);
  const acquisitionAvailableDates = useSelector((state) => state.acquisition.availableDates);
  const imageList = useSelector((state) => state.euscoast.imageList);
  const periodLabels = useSelector((state) => state.euscoast.periodLabels);
  const periodLabel = (key) => periodLabels[key] ?? key;
  const coordinates = useSelector((state) => state.acquisition.coordinates);
  /**
   * The name of the KML the area came from, when it came from one. The export is named after it, so
   * a folder of saved analyses says which stretch of coast each one is.
   */
  const kmlFileName = useSelector((state) => state.acquisition.kmlFileName);
  const [finished, setFinished] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [baselineBufferReady, setBaselineBufferReady] = useState(false);

  const [mapReady, setMapReady] = useState(false);

  const restoredAnalysisRef = useRef(false);
  const [transects, setTransects] = useState(5);
  const [transectsScale, setTransectsScale] = useState(100);
  const [transectsData, setTransectsData] = useState([]);
  const [transectsResults, setTransectsResults] = useState(false);
  const [transectsObjects, setTransectsObjects] = useState([]);
  const [transectsCsv, setTransectsCsv] = useState('');
  const [transectsCalcs, setTransectsCalcs] = useState({});
  const [showHelpDialogVisibility, setShowHelpDialogVisibility] = useState(false);
  const [showHelpDialogCSV, setShowHelpDialogCSV] = useState(false);
  const [showHelpDialogShp, setShowHelpDialogShp] = useState(false);
  const [showHelpDialogVector, setShowHelpDialogVector] = useState(false);
  const [selectedTransect, setSelectedTransect] = useState(1);
  const [previsao, setPrevisao] = useState(10);
  const [years, setYears] = useState([]);
  const [opacity, setOpacity] = useState({
    0: { overlay: 0, raster: 1 },
    1: { overlay: 0, raster: 0 },
    2: { transects: true, baseline: true },
  });
  const [calcMessage, setCalcMessage] = useState(t('forms.euscoast.result.loading.preparing'));
  const [showLegendCard, setShowLegendCard] = useState(false);
  const [restoredBaseLineCoords, setRestoredBaseLineCoords] = useState([]);

  /**
   * @function displayROI
   * @description Responsible for showing on screen the region of interest (ROI)
   * **Called by:
   * @see {ProcessingPage} - via the GoogleMap's onLoad callback.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const displayROI = () => {
    if (!coordinates) return;
    dispatch(Map.centralizeMap(coordinates));
    const formatedCoordinates = aoiPaths(coordinates);
    ROI.current = new window.google.maps.Polygon({
      paths: formatedCoordinates,
      geodesic: true,
      fillColor: 'transparent',
      strokeColor: '#00A391',
      strokeOpacity: 0.6,
      strokeWeight: 5,
      clickable: false,
    });
    ROI.current.setMap(mapRef.current);
    boundsRef.current = new window.google.maps.LatLngBounds();
    ROI.current
      .getPaths()
      .getArray()
      .flatMap((path) => path.getArray())
      .forEach((coord) => {
        let latLng = new window.google.maps.LatLng(coord.lat(), coord.lng());
        boundsRef.current.extend(latLng);
      });
    fitBoundsWithOverlayPadding(mapRef.current, boundsRef.current);
  };

  /**
   * Redraws and reframes the area when it arrives after the map has already loaded. A session
   * import sets the coordinates at that point, and without this the area was drawn but never
   * framed, leaving the user looking at the world while the analysis sat somewhere off screen.
   * @see {@link displayROI}
   * @memberof module:Euscoast
   */
  useEffect(() => {
    if (!mapRef.current || !coordinates?.length) return;
    ROI.current?.setMap(null);
    displayROI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinates]);


  useEffect(() => {
    loadScript('google-charts', 'https://www.gstatic.com/charts/loader.js', () => {
      window.google.charts.load('current', {
        packages: ['corechart', 'bar', 'table'],
      });
    });
  }, []);

  useEffect(() => {
    if (!mapReady) return;
    const values = Object.values(images);
    if (values.length > 0) {
      let tempYears = [];
      values.forEach((image, i) => {
        if (image.overlay !== undefined && image.classification !== undefined) {
          image.overlay.setMap(mapRef.current);
          image.overlay.setOpacity(0);
          image.classification.setMap(mapRef.current);
          image.classification.setOpacity(i === values.length - 1 ? 1 : 0);
        } else {
          console.error('Image without overlay', image, i);
        }
        tempYears.push(image.date);
      });
      setYears(tempYears);
    }
  }, [images, mapReady]);

  /**
   * @description Derives each transect's coastal squeeze projection whenever the raw
   * per-year land-cover intersection results (or the forecast horizon) change.
   * 1. For every transect, groups its per-year land-cover intersections and computes each
   *    land-cover's rate of change (length/distance delta divided by the year gap).
   * 2. Projects each land-cover's length `previsao` years into the future using that rate,
   *    building one CSV/table row per year (plus the projected year).
   * 3. Reconciles the projected values (vegetation eating into mangrove, water rise displacing
   *    mangrove, water/human area budget) and classifies the transect as full squeeze (mangrove
   *    reaches ~0), partial squeeze, or no squeeze — coloring its map polyline accordingly
   *    (red/orange/green/gray).
   * 4. Accumulates the per-transect rows into the exportable CSV string and re-wires the
   *    transect polyline's hover/click listeners.
   * @memberof module:Euscoast
   */
  useEffect(() => {
    if (Object.keys(transectsResults).length <= 0 || years.length < 2) return;
    let tempTransectsCalcs = {};
    const periodKeys = [...years].sort(comparePeriodKeys);
    const baseYears = periodKeys.map((key) => parseInt(key.slice(0, 4), 10));
    const firstYear = baseYears[0];
    const lastYear = baseYears[baseYears.length - 1];
    const yearsDifference = Math.max(lastYear - firstYear, 1);
    let numberTransects = Object.values(transectsResults)[0].length;
    let flattenResults = Object.values(transectsResults).flat();
    let csvData = `${t('forms.euscoast.result.labels.id')};`;
    let csvYears = [...periodKeys.map((key) => periodLabel(String(key))), String(lastYear + Number(previsao))];
    for (let csvYear of csvYears) {
      for (let landCover of flattenResults[0].landCoversIntersections) {
        csvData += `${csvYear}_${t(`forms.euscoast.result.labels.${landCover.label}`)};`;
      }
    }
    csvData = csvData.slice(0, -1) + `\n`;
    for (let transect = 1; transect <= numberTransects; transect++) {
      if (flattenResults[transect] === null) continue;
      let centerCoords,
        landcoversOutPut = {},
        rate = {
          water: null,
          human: null,
          mangroove: null,
          vegetation: null,
        },
        extra = {
          humanLastYear: null,
          mangrooveLastYear: null,
        };
      const mapObject = transectsObjects.filter((object) => object.transect === transect)[0];
      if (!mapObject) continue;
      const dataTransect = flattenResults
        .filter((result) => result.id === transect)
        .sort((a, b) => comparePeriodKeys(b.year, a.year));
      dataTransect.forEach((data, index) => {
        centerCoords = data.center;
        data.landCoversIntersections.forEach((land) => {
          if (landcoversOutPut[land.label] === undefined)
            landcoversOutPut[land.label] = {
              id: land.label,
              landcover: land.classe,
              years: [],
              completeLine: data.completeLine,
            };
          let length = Number(land.lengthSide.reduce((a, b) => a + b).toFixed(2));
          let shoreLineDistance = Number(land.shoreLineDistance.sort()[1].toFixed(2));
          let baseLineDistance = Number(land.baseLineDistance.sort()[0].toFixed(2));
          switch (land.label) {
            case 0:
              if (index === 0) extra.mangrooveLastYear = length;
              if (rate.mangroove === null) {
                rate.mangroove = length;
              } else {
                rate.mangroove -= length;
              }
              break;
            case 1:
              if (rate.vegetation === null) {
                rate.vegetation = length;
              } else {
                rate.vegetation -= length;
              }
              break;
            case 2:
              if (rate.water === null) {
                rate.water = baseLineDistance;
              } else {
                rate.water -= baseLineDistance;
              }
              break;
            case 3:
              if (index === 0) extra.humanLastYear = shoreLineDistance;
              if (rate.human === null) {
                rate.human = shoreLineDistance;
              } else {
                rate.human = rate.human - shoreLineDistance;
              }
              break;
            default:
              break;
          }
          landcoversOutPut[land.label].years.push({
            year: data.year,
            length,
            completeLine: data.completeLine,
          });
        });
      });
      let rowsTemp = {};
      Object.values(landcoversOutPut)
        .sort((l1, l2) => l2.id - l1.id)
        .forEach((land) => {
          if (land.id === 2) return;
          let id;
          switch (land.id) {
            case 0:
              id = 2;
              break;
            case 1:
              id = 1;
              break;
            case 2:
              id = 3;
              break;
            case 3:
              id = 0;
              break;
            case 4:
              id = 4;
              break;
            default:
              break;
          }
          land.years
            .sort((a, b) => a.year - b.year)
            .forEach((year) => {
              if (rowsTemp[year.year] === undefined) rowsTemp[year.year] = [0.0, 0.0, 0.0, 0.0, 0.0];
              rowsTemp[year.year][id] = parseFloat(year.length);
            });
          if (rowsTemp[lastYear + previsao] === undefined) rowsTemp[lastYear + previsao] = [0.0, 0.0, 0.0, 0.0, 0.0];
          let tempRateNumber = land.years
            .sort((a, b) => comparePeriodKeys(a.year, b.year))
            .reduce((a, b) => b.length - a.length);
          let rate = tempRateNumber / yearsDifference;
          let yearLength = land.years[1] !== undefined ? land.years[1].length : 0;
          let numberFuture = (rate * previsao + yearLength).toFixed(2);
          rowsTemp[lastYear + previsao][id] = parseFloat(numberFuture > 0 ? numberFuture : 0);
        });
      Object.values(landcoversOutPut)
        .sort((l1, l2) => l2.id - l1.id)
        .forEach((land) => {
          if (land.id !== 2) return;
          let id = 3;
          land.years
            .sort((a, b) => a.year - b.year)
            .forEach((year) => {
              if (rowsTemp[year.year] === undefined) rowsTemp[year.year] = [0.0, 0.0, 0.0, 0.0, 0.0];
              rowsTemp[year.year][id] = parseFloat(year.length);
            });
          if (rowsTemp[lastYear + previsao] === undefined) rowsTemp[lastYear + previsao] = [0.0, 0.0, 0.0, 0.0, 0.0];
          let tempRateNumber = land.years
            .sort((a, b) => comparePeriodKeys(a.year, b.year))
            .reduce((a, b) => b.length - a.length);
          let rate = tempRateNumber / yearsDifference;
          let yearLength = land.years[1] !== undefined ? land.years[1].length : 0;
          let numberFuture = (rate * previsao + yearLength).toFixed(2);
          rowsTemp[lastYear + previsao][id] = parseFloat(numberFuture > 0 ? numberFuture : 0);
        });
      const originalTransectLength = rowsTemp[lastYear].reduce((a, b) => a + b, 0);
      const transectLength = rowsTemp[lastYear].reduce((a, b) => a + b, 0) + yearsDifference;
      if (transectLength > originalTransectLength) {
        let availableArea = originalTransectLength - rowsTemp[lastYear][3];
        if (rowsTemp[lastYear + previsao][0] > availableArea) {
          rowsTemp[lastYear + previsao][0] = availableArea;
        }
      }
      let rowsInfos = [...periodKeys, String(lastYear + Number(previsao))]
        .map((key) => [periodLabel(key), rowsTemp[key]])
        .filter(([, row]) => row !== undefined);
      let rows = [
        [
          t('forms.euscoast.year'),
          t('forms.euscoast.landcovers.human'),
          t('forms.euscoast.landcovers.vegetation'),
          t('forms.euscoast.landcovers.mangrove'),
          t('forms.euscoast.landcovers.water'),
          t('forms.euscoast.landcovers.beach'),
        ],
      ];
      for (let x = 0; x < rowsInfos.length; x++) {
        let rowInfo = rowsInfos[x];
        rows.push([rowInfo[0], ...rowInfo[1]]);
      }
      rows[3][3] = rows[2][3];
      let futureVegetation = rows[2][2] - (rows[3][1] - rows[2][1]);
      rows[3][2] = futureVegetation > 0 ? futureVegetation : 0;
      if (futureVegetation < 0) {
        let finalMangue = rows[3][3] + futureVegetation;
        rows[3][3] = finalMangue > 0 ? finalMangue : 0;
      }
      let diffWater = rows[2][4] - rows[1][4];
      let rateWater = diffWater / yearsDifference;
      let waterResult = rows[2][4] + rateWater * previsao;
      rows[3][4] = waterResult > 0 ? waterResult : 0;
      if (waterResult > rows[2][4]) {
        let diffMangue = rows[3][3] - (waterResult - rows[2][4]);
        rows[3][3] = diffMangue > 0 ? diffMangue : 0;
      }
      mapObject.setOptions({ strokeColor: classifyUrbanGrowth(rows).color });
      let yearData = `${transect};`;
      for (let r = 1; r < rows.length; r++) {
        for (let c = 1; c < rows[r].length; c++) {
          yearData += `${rows[r][c].toFixed(2)};`;
        }
      }
      csvData += yearData.slice(0, -1) + `\n`;
      for (let r = 0; r < rows.length; r++) {
        if (tempTransectsCalcs[transect] === undefined) tempTransectsCalcs[transect] = [];
        tempTransectsCalcs[transect].push(rows[r]);
      }
      setTransectsCalcs({ ...tempTransectsCalcs });
      window.google.maps.event.clearInstanceListeners(mapObject.polyline);
      window.google.maps.event.addListener(mapObject.polyline, 'mouseover', () => {
        mapObject.setOptions({ strokeWeight: 9 });
      });
      window.google.maps.event.addListener(mapObject.polyline, 'mouseout', () => {
        mapObject.setOptions({ strokeWeight: 3 });
      });
      window.google.maps.event.addListener(mapObject.polyline, 'click', () => {
        setSelectedTransect(transect);
      });
    }
    setTransectsCsv(csvData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transectsResults, previsao, transectsObjects, i18n.language]);

  /**
   * @description Runs the per-transect, per-year land-cover intersection analysis once both
   * years' classified land-cover/water vectors are available. Buffers the drawn baseline by the
   * transect extent, extracts each year's shoreline from the water vector, then computes each
   * transect's land-cover intersections for that year, updating the progress message as it goes.
   * The `transectsData.length === 0` guard keeps a stale empty closure (an already-vectorized
   * session, or the analyzing flip arriving in the same render as the transects) from running
   * the analysis against no transects.
   * @see {@link extractShoreLineTurf}
   * @see {@link landCoversIntersectionsTurf}
   * @memberof module:Euscoast
   */
  useEffect(() => {
    if (!analyzing || !baselineBufferReady || transectsData.length === 0) return;
    if (Object.values(classLandCoversVectors).length !== 2 || waterLandCoverVectors.length !== 2) return;
    let results = {};
    let yearsArray = Object.values(years);
    (async () => {
      for (let i = 0; i < yearsArray.length; i++) {
        const year = yearsArray[i];
        results[year] = [];
        let yearClass = classLandCoversVectors[year];
        let waterGeometry = waterLandCoverVectors.filter((water) => water.year === year)[0].geometry;
        let shoreLine = extractShoreLineTurf(baselineBufferRef.current, waterGeometry);
        setCalcMessage(t('forms.euscoast.result.output.organizeYear').replace('{year}', periodLabel(year)));
        let messageTranslate = t('forms.euscoast.result.output.calculatingTransect');
        await timer(10);
        for (let t = 0; t < transectsData.length; t++) {
          const transect = transectsData[t];
          let message = `${periodLabel(year)}: ${messageTranslate} ${transect.transect}/${transectsData.length}`;
          setCalcMessage(message);
          let landCoversResult = landCoversIntersectionsTurf(transect, yearClass, shoreLine, year);
          await timer(10);
          results[year].push(landCoversResult);
        }
        setCalcMessage(t('forms.euscoast.result.output.calculatingComplete').replace('{year}', periodLabel(year)));
      }
      setTransectsResults(results);
      setFinished(true);
      setCalcMessage(t('forms.euscoast.result.output.ready'));
      setShowLegendCard(true);
      setSelectedTransect(2);
      setTimeout(() => {
        setSelectedTransect(1);
        setAnalyzing(false);
      }, 2000);
    })();
  }, [analyzing, baselineBufferReady, transectsData, classLandCoversVectors, waterLandCoverVectors]);

  useEffect(() => {
    transectsObjects.forEach((obj) => {
      if (obj.transect === parseInt(selectedTransect)) {
        const bounds = new window.google.maps.LatLngBounds();
        obj
          .getPath()
          .getArray()
          .forEach((coord) => {
            let latLng = new window.google.maps.LatLng(coord.lat(), coord.lng());
            bounds.extend(latLng);
          });
        const center = bounds.getCenter();
        transectMarker.current.setMap(mapRef.current);
        transectMarker.current.setLabel(`${selectedTransect}`);
        transectMarker.current.setPosition(center);
        transectMarker.current.setVisible(true);
        obj.setOptions({ strokeWeight: 9 });
      } else obj.setOptions({ strokeWeight: 3 });
    });
  }, [selectedTransect]);

  /**
   * @function startCalc
   * @description Kicks off the coastal squeeze analysis. Generates transects along the baseline
   * using turf.js, creates map polylines for them, computes the baseline buffer, and dispatches
   * the vectorization request if the classified land-cover vectors for both years aren't cached
   * yet — `transformClassToPolygons` populates `classLandCoversVectors`/`waterLandCoverVectors`,
   * which triggers the analysis effect.
   * @see {@link generateTransects}
   * @see {@link timer}
   * **Called by:
   * @see {AnalysisSteps} - via the onAnalysisPerform callback when the ANALYSIS_DONE step is reached.
   * @memberof module:Euscoast
   * @returns {Promise<void>}
   */
  /**
   * @function createTransectObjects
   * @description Draws a transect list on the map and returns the handles the rest of the page
   * works through. A transect is held as two sides measured out from the baseline; on the map it is
   * the single line joining them.
   * @param {Array<Object>} transectsList - `{transect, center, sides}` entries.
   * **Called by:
   * @see {runCalc} - after generating them.
   * @see {the imported-session restore} - after reading them back.
   * @memberof module:Euscoast
   * @returns {Array<Object>} One handle per transect.
   */
  const createTransectObjects = (transectsList) =>
    (transectsList || []).map((transect) => {
      const side1 = transect.sides[0].coords.map(([lng, lat]) => ({ lat, lng }));
      const side2 = transect.sides[1].coords.map(([lng, lat]) => ({ lat, lng }));
      const polyline = new window.google.maps.Polyline({
        path: [...side1, ...side2],
        geodesic: true,
        strokeColor: '#00A391',
        strokeOpacity: 0.8,
        strokeWeight: 3,
        map: mapRef.current,
      });
      return {
        transect: transect.transect,
        polyline,
        getPath: polyline.getPath.bind(polyline),
        setOptions: polyline.setOptions.bind(polyline),
        setVisible: polyline.setVisible.bind(polyline),
        setMap: polyline.setMap.bind(polyline),
      };
    });

  /**
   * @description Brings a finished analysis back from an imported session.
   * The import used to hand over the baseline alone and leave the user to walk the steps again -
   * draw it, confirm it, configure the transects, run the analysis - for a session that had already
   * been through all of that. What it needs back is what the run produced: the transects and the
   * measurements taken along them. The effect that builds the tables runs on `transectsResults`
   * and does the whole job from there, so handing it those leaves the page as a finished run, with
   * the forecast selector still able to recompute.
   * Waits for the years, which the imagery effect sets: the tables are laid out per year. Reading
   * the pending payload also clears it, so it must not be read before it can be used.
   * @memberof module:Euscoast
   */
  useEffect(() => {
    if (!mapReady || restoredAnalysisRef.current) return;
    if (!years.length && Object.keys(images || {}).length) return;

    const pending = readPendingBaseline();
    if (!pending?.baseLineCoords?.length) return;
    restoredAnalysisRef.current = true;

    if (pending.transectsScale) setTransectsScale(pending.transectsScale);
    if (pending.transects) setTransects(pending.transects);
    if (pending.forecastYears) setPrevisao(pending.forecastYears);

    const hasAnalysis = Boolean(pending.transectsData?.length) && Boolean(Object.keys(pending.transectsResults || {}).length);

    if (!hasAnalysis) {
      setRestoredBaseLineCoords(pending.baseLineCoords);
      return;
    }

    const baselineOverlay = new window.google.maps.Polyline({
      path: pending.baseLineCoords.map(([lng, lat]) => ({ lat, lng })),
      geodesic: true,
      strokeColor: 'yellow',
      strokeOpacity: 1,
      strokeWeight: 3,
      map: mapRef.current,
    });
    baselineRef.current = { overlay: baselineOverlay };

    setTransectsData(pending.transectsData);
    setTransectsObjects(createTransectObjects(pending.transectsData));
    setTransectsResults(pending.transectsResults);
    setFinished(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, years, images]);

  const startCalc = async (baseLineCoords, transectsStep, transectsExtent, threshold, baseline) => {
    try {
      await runCalc(baseLineCoords, transectsStep, transectsExtent, threshold, baseline);
    } catch (error) {
      console.error('[EUSCoast] the transect analysis could not be started', error);
      setAnalyzing(false);
      setCalcMessage(t('forms.euscoast.result.loading.preparing'));
      notifyError(error, { fallback: t('forms.euscoast.result.loading.failed') });
    }
  };

  /**
   * @function runCalc
   * @description The body of {@link startCalc}, kept apart so every way it can fail is caught in
   * one place. Anything thrown in here used to leave `analyzing` true for good: the panel sat on
   * "preparing" with no error, which reads as the analysis having silently stopped.
   * @param {Array<Array<Number>>} baseLineCoords - The drawn baseline, as `[lng, lat]` pairs.
   * @param {Number} transectsStep - Spacing between transects, in metres.
   * @param {Number} transectsExtent - Length of each transect, in metres.
   * @param {Number} threshold - Water/land threshold carried through to the analysis.
   * @param {Object} baseline - The baseline overlay, kept for later redraws.
   * **Called by:
   * @see {startCalc}
   * @memberof module:Euscoast
   * @returns {Promise<void>}
   */
  const runCalc = async (baseLineCoords, transectsStep, transectsExtent, threshold, baseline) => {
    setBaselineBufferReady(false);
    setAnalyzing(true);
    setCalcMessage(t('forms.euscoast.result.loading.preparing'));
    await timer(50);
    baselineRef.current = baseline;
    transectsObjects.forEach((obj) => obj.setMap && obj.setMap(null));
    const newTransectsData = generateTransects(baseLineCoords, transectsStep, transectsExtent);
    setTransectsData(newTransectsData);
    setTransectsObjects(createTransectObjects(newTransectsData));
    let baseLineTurf = turf.lineString(baseLineCoords);
    baselineBufferRef.current = turf.buffer(baseLineTurf, transectsExtent, {
      units: 'meters',
    });
    setBaselineBufferReady(true);
    if (Object.values(classLandCoversVectors).length !== 2 || waterLandCoverVectors.length !== 2) {
      const classifiedImages = Object.values(images || {});
      if (!classifiedImages.length || !coordinates?.length) {
        throw new Error('EUSCoast: no classified images or area available to vectorize.');
      }

      classifiedImages.forEach((image) => {
        dispatch(
          EUSCoast.transformClassToPolygons(
            image.classified,
            image.imageObject.projection(),
            image.date,
            image.type,
            coordinates,
          ),
        );
      });
    }
  };

  /**
   * @function generateTransects
   * @description Generates evenly-spaced transects perpendicular to the drawn baseline
   * using turf.js. For each sample point along the baseline at the given step distance,
   * computes the baseline bearing and creates perpendicular transect sides of the given extent.
   * @param {Array} baseLineCoords - Array of [lng, lat] coordinate pairs for the baseline.
   * @param {number} step - Distance in meters between consecutive transects along the baseline.
   * @param {number} extent - Total length in meters of each transect perpendicular to the baseline.
   * @memberof module:Euscoast
   * @returns {Array<Object>} Transects: {transect, center, sides: [{side, coords}, {side, coords}]}.
   */
  const generateTransects = (baseLineCoords, step, extent) => {
    const turfLine = turf.lineString(baseLineCoords);
    setTransectsScale(step);
    const lineLength = turf.length(turfLine, { units: 'meters' });
    const points = [];
    for (let d = 0; d <= lineLength; d += step) {
      points.push({ point: turf.along(turfLine, d, { units: 'meters' }), dist: d });
    }
    const outputTransects = [];
    points.forEach(({ point, dist }, i) => {
      if (i === 0) return;
      const pointCoords = point.geometry.coordinates;
      const prevDist = Math.max(0, dist - 1);
      const nextDist = Math.min(lineLength, dist + 1);
      const prevPoint = turf.along(turfLine, prevDist, { units: 'meters' });
      const nextPoint = turf.along(turfLine, nextDist, { units: 'meters' });
      const bearing = turf.bearing(prevPoint, nextPoint);
      const halfExtent = extent / 2;
      const end1 = turf.destination(pointCoords, halfExtent, bearing + 90, { units: 'meters' });
      const end2 = turf.destination(pointCoords, halfExtent, bearing - 90, { units: 'meters' });
      outputTransects.push({
        transect: i,
        center: [pointCoords[1], pointCoords[0]],
        sides: [
          { side: 1, coords: [end1.geometry.coordinates, pointCoords] },
          { side: 2, coords: [pointCoords, end2.geometry.coordinates] },
        ],
      });
    });
    return outputTransects;
  };

  /**
   * @function handlePrevisao
   * @description Updates the forecast horizon (years into the future to project).
   * @param {Event} event - Change event from the forecast input.
   * **Called by:
   * @see {ProcessingPage} - via each forecast-year Radio's onChange.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const handlePrevisao = (event) => {
    let value = Math.abs(parseInt(event.target.value));
    if (typeof value !== 'number' || isNaN(value)) return setPrevisao(1);
    if (value > 0) setPrevisao(value);
  };

  /**
   * @function opacityChange
   * @description Sets a GroundOverlay's opacity.
   * @param {Number} level - Opacity level (0-1).
   * @param {google.maps.GroundOverlay} image - Overlay to update.
   * **Called by:
   * @see {ProcessingPage} - via the true-color/false-color/classification opacity sliders.
   * @memberof module:Euscoast
   * @returns {Number} The applied opacity level.
   */
  const opacityChange = (level, image) => {
    image.setOpacity(level);
    return level;
  };

  /**
   * @function opacityBaseLineChange
   * @description Toggles the drawn baseline's visibility on the map via the baselineRef.
   * @param {boolean} [state] - Desired visibility; toggles the current state if omitted.
   * **Called by:
   * @see {ProcessingPage} - via the baseline visibility toggle.
   * @memberof module:Euscoast
   * @returns {boolean} The applied visibility.
   */
  const opacityBaseLineChange = (state) => {
    const baseline = baselineRef.current;
    if (!baseline?.overlay) return state ?? false;
    let visible = state ?? !baseline.overlay.visible;
    baseline.overlay.setVisible(visible);
    return visible;
  };

  /**
   * @function opacityTransectsChange
   * @description Toggles every transect polyline's visibility on the map.
   * @param {boolean} [state] - Desired visibility; toggles the current state if omitted.
   * **Called by:
   * @see {ProcessingPage} - via the transects visibility toggle.
   * @memberof module:Euscoast
   * @returns {boolean} The applied visibility.
   */
  const opacityTransectsChange = (state) => {
    transectsObjects.forEach((transect) => {
      transect.setVisible(state ?? !opacity[2].transects);
    });
    return !opacity[2].transects;
  };

  /**
   * @function exportToCsv
   * @description Triggers a browser download of the given CSV content.
   * @param {String} filename - Download filename.
   * @param {String} csv - CSV content to download.
   * **Called by:
   * @see {ProcessingPage} - via the "Export CSV" button's onClick.
   * @memberof module:Euscoast
   * @returns {void}
   */
  function exportToCsv(filename, csv) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, filename);
    } else {
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  /**
   * @function handleShapefileExport
   * @description Exports the baseline and transects (enriched with their computed CSV data) as
   * a shapefile group.
   * @see {@link parseCsv}
   * **Called by:
   * @see {ProcessingPage} - via the "Export shapefile" button's onClick.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const handleShapefileExport = () => {
    try {
      const transectsCsvData = parseCsv(transectsCsv);
      /**
       * @function getTransectDataFromCsv
       * @description Looks up a transect's parsed CSV row by its 1-based transect number.
       * @param {Number} index - 1-based transect number.
       * **Called by:
       * @see {handleShapefileExport}
       * @memberof module:Euscoast
       * @returns {Object} The transect's parsed CSV row.
       */
      const getTransectDataFromCsv = (index) => {
        return transectsCsvData[index - 1];
      };
      const baselineOverlay = baselineRef.current?.overlay;
      const baselinePath = baselineOverlay?.getPath()?.getArray();
      if (!baselinePath) return;
      const baselineShp = {
        features: [
          {
            geometry: {
              type: 'LineString',
              coordinates: baselinePath.map((coord) => [coord.lng(), coord.lat()]),
            },
            properties: {
              name: 'Baseline',
            },
          },
        ],
      };
      const transectsShp = {
        features: transectsData.map((transect) => {
          const csvData = getTransectDataFromCsv(transect.transect);
          return {
            geometry: {
              type: 'LineString',
              coordinates: [...transect.sides[0].coords, ...transect.sides[1].coords.slice().reverse()].map((coord) => [
                coord[0],
                coord[1],
              ]),
            },
            properties: {
              ...csvData,
            },
          };
        }),
      };
      Export.table.toDevice.asShapefileGroup(
        [baselineShp, transectsShp],
        Export.defaultOptions.device.shapefileGroup('layers', 'baseline', 'transects'),
      );
    } catch (error) {
      console.error('Erro ao exportar shapefiles:', error);
    }
  };

  /**
   * @function handleVectorExport
   * @description Exports each year's classified land-cover raster as a polygon shapefile, one
   * layer per year, so the classification can be opened in QGIS. Uses `classLandCoverPolygons`,
   * the undissolved output of the vectorization the analysis already asked Earth Engine for, so
   * every painted area becomes its own record - tagged with the year, its class label and the
   * translated class name - instead of one merged shape per class. Each layer ships with the QGIS
   * style that paints it by class, so it opens looking like the raster.
   * @see {@link buildLandCoverStyle}
   * @see {@link geometryToPolygons}
   * **Called by:
   * @see {ProcessingPage} - via the "Export raster as vector" button's onClick.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const handleVectorExport = () => {
    try {
      const vectorizedYears = Object.keys(classLandCoverPolygons);
      const collections = vectorizedYears.map((year) => ({
        features: (classLandCoverPolygons[year] || []).flatMap((polygon) => {
          const id = polygon.properties?.label;
          return geometryToPolygons(polygon.geometry).map((coordinates) => ({
            geometry: {
              type: 'Polygon',
              coordinates,
            },
            properties: {
              year: Number(year),
              class: id,
              name: LAND_COVER_KEYS[id] ? t(`forms.euscoast.landcovers.${LAND_COVER_KEYS[id]}`) : String(id),
            },
          }));
        }),
      }));
      const styles = collections.map((collection) =>
        buildLandCoverStyle(
          [...new Set(collection.features.map((feature) => feature.properties.class))]
            .filter((value) => LAND_COVER_KEYS[value])
            .sort()
            .map((value) => ({ value, label: t(`forms.euscoast.landcovers.${LAND_COVER_KEYS[value]}`) })),
        ),
      );
      Export.table.toDevice.asShapefileGroup(collections, {
        ...Export.defaultOptions.device.shapefileGroup(
          'landcover',
          ...vectorizedYears.map((year) => `landcover_${year}`),
        ),
        styles,
      });
    } catch (error) {
      console.error('Erro ao exportar o raster vetorizado:', error);
    }
  };

  /**
   * @function handleRestartAnalysis
   * @description Clears the vectors, transects, results and refs so the step flow can start over.
   * **Called by:
   * @see {AnalysisSteps} - via the restart button once the ANALYSIS_DONE step is reached.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const handleRestartAnalysis = () => {
    dispatch(EUSCoast.resetWaterLandCover());
    dispatch(EUSCoast.resetClassLandCovers());
    setAnalyzing(false);
    setFinished(false);
    setShowLegendCard(false);
    baselineRef.current = null;
    baselineBufferRef.current = [];
    setBaselineBufferReady(false);
    transectsObjects.forEach((transect) => {
      transect.setMap(null);
    });
    if (transectMarker.current?.setMap) transectMarker.current.setMap(null);
    setTransectsResults([]);
    setTransectsData([]);
    setTransectsCsv('');
    setTransectsObjects([]);
  };

  /**
   * @function exportSessionState
   * @description Downloads a JSON snapshot of the current analysis's configuration (satellite,
   * AOI, years/dates/missions, training polygons, baseline/transects and any already-vectorized
   * land covers), for EUSCoast's "import session" icon to replay later — a developer/testing
   * shortcut so QA doesn't have to redo the whole wizard every time they need a completed
   * classification to test against.
   * @see {@link buildSessionExport}
   * **Called by:
   * @see {NavigationBar} - via the "export session for testing" icon's onClick.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const exportSessionState = () => {
    exportSession(
      buildSessionExport({
        images,
        years: euscoastYears,
        dates: euscoastDates,
        missions: euscoastMissions,
        coordinates,
        availableDates: acquisitionAvailableDates,
        imageList,
        baseLine: baselineRef.current,
        transects,
        transectsData,
        transectsResults,
        transectsCalcs,
        forecastYears: previsao,
        transectsScale,
        classLandCoversVectors,
        classLandCoverPolygons,
        waterLandCoverVectors,
      }),
      kmlFileName,
    );
  };

  const hasImages = images && Object.keys(images).length > 0;
  useRegisterSessionExport('euscoast', hasImages ? exportSessionState : null);

  /**
   * @function exportGeoJsonLayers
   * @description Writes the analysis's vector layers - area, baseline, transects, and each period's
   * classified land cover and water - to a GeoJSON file for a GIS. The periods are written under
   * their labels, so two periods of one year are told apart by the dates they were acquired rather
   * than by a `-1` suffix.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const exportGeoJsonLayers = () => {
    if (
      !exportGeoJson({
        coordinates,
        baseLine: baselineRef.current,
        transectsData,
        classLandCoversVectors,
        waterLandCoverVectors,
        periodLabels,
      })
    ) {
      notifyError(t('forms.acquisition.geoJsonExport.empty'));
    }
  };
  useRegisterGeoJsonExport('euscoast', hasImages ? exportGeoJsonLayers : null);

  /**
   * @function reportText
   * @description Every piece of wording the report needs, translated here and handed to the
   * builder, which stays a pure function of the analysis - see {@link buildLandCoverReport}.
   * @memberof module:Core
   * @returns {Object}
   */
  const reportText = () => {
    const key = (name) => `forms.core.pdfReport.${name}`;
    const own = (name) => `forms.core.pdfReport.euscoast.${name}`;
    const group = (name) => t(own(name), { returnObjects: true, defaultValue: t(key(name), { returnObjects: true }) });
    return {
      ...t(key('common'), { returnObjects: true }),
      ...t(own('text'), { returnObjects: true }),
      subtitle: t(key('subtitle'), { date: new Date().toLocaleString() }),
      missingImage: (caption) => t(key('missingImage'), { caption }),
      screenCaption: t(key('screenCaption')),
      rates: group('rates'),
      classes: group('classes'),
      transects: group('transects'),
      transectNote: {
        complete: (values) => t(key('transectComplete'), values),
        truncated: (values) => t(key('transectTruncated'), values),
      },
      transectComplete: (values) => t(key('transectComplete'), values),
      transectTruncated: (values) => t(key('transectTruncated'), values),
      forecastYears: (values) => t(key('forecastYears'), values),
      missingTideNote: (values) => t(own('missingTideNote'), values),
      tide: {
        range: (values) => t(own('tideRangeValue'), values),
        span: (values) => t(own('tideSpanValue'), values),
      },
      errors: group('errors'),
      byDate: group('byDate'),
      byTransect: group('byTransect'),
      bays: group('bays'),
      controlPoints: group('controlPoints'),
      waterlines: group('waterlines'),
      cover: group('cover'),
    };
  };

  /**
   * @function exportPdfReport
   * @description Writes the whole analysis to one PDF.
   * The map is captured from the screen rather than fetched, because what this module draws is a
   * Google Maps overlay and not an image with a URL - see {@link captureImageForReport}.
   * @memberof module:Core
   * @returns {Promise<void>}
   */
  const exportPdfReport = async () => {
    try {
      const text = reportText();
      /**
       * The map is captured from the screen, so it is framed on the analysis first - otherwise the
       * report shows wherever the user had last panned to. See {@link settleMapForCapture}, which
       * also waits for the tiles of the new view to arrive.
       */
      await settleMapForCapture(mapRef.current, () => fitCoordinatesWithOverlayPadding(mapRef.current, coordinates));
      const screen = await captureImageForReport(APP_ROOT_SELECTOR, text.screenCaption);
      const report = buildLandCoverReport({
        coordinates,
        transectsData,
        transectsCalcs,
        periodLabels,
        images: [screen],
        text,
      });
      renderReport(report).save(reportFileName(REPORT_MODULE));
    } catch (error) {
      console.error('exportPdfReport error:', error);
      notifyError(t('forms.core.pdfReport.failed'));
    }
  };
  useRegisterPdfExport(REPORT_MODULE, hasImages ? exportPdfReport : null);


  return (
    <ThemeProvider theme={euscoastTheme}>
      <Box className={classes.wrapper}>
        <Grid container justifyContent="space-between" id="mapScreen" className={classes.mapOverlay}>
          <Grid
            item
            xs={3}
            className={classes.mapContainer}
            style={{
              flexDirection: 'column',
              justifyContent: 'space-around',
              height: '100%',
              maxWidth: '100%',
              maxHeight: OVERLAY_COLUMN_MAX_HEIGHT_SAFE,
              msOverflowStyle: 'none',
            }}>
            <AnalysisSteps
              title={'forms.euscoast.result.steps'}
              steps={['DRAW_BASELINE', 'CONFIGURE_TRANSECTS', 'ANALYSIS_DONE']}
              t={t}
              mapRef={mapRef}
              roiCoords={ROI}
              drawingManager={drawingManager}
              initialBaselineCoords={restoredBaseLineCoords}
              onAnalysisPerform={startCalc}
              onRestartAnalysis={handleRestartAnalysis}
            />
            {finished && transectsResults && (
              <Box display="flex" flexDirection="column" style={{ gap: 10, width: '100%' }}>
                <Card style={{ padding: 15 }} elevation={3}>
                  <Typography fontSize={20} style={{ fontWeight: 'bold', marginBottom: 5 }}>
                    {t('forms.euscoast.result.forecast')}
                  </Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center" style={{ gap: 4 }}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        textAlign: 'center',
                        alignItems: 'center',
                      }}>
                      <Radio
                        checked={previsao === 10}
                        onChange={handlePrevisao}
                        value="10"
                        name="radio-button-demo"
                        inputProps={{ 'aria-label': 10 }}
                      />
                      <Typography variant="caption">10 {t('forms.euscoast.years')}</Typography>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        textAlign: 'center',
                        alignItems: 'center',
                      }}>
                      <Radio
                        checked={previsao === 25}
                        onChange={handlePrevisao}
                        value="25"
                        name="radio-button-demo"
                        inputProps={{ 'aria-label': 25 }}
                      />
                      <Typography variant="caption">25 {t('forms.euscoast.years')}</Typography>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        textAlign: 'center',
                        alignItems: 'center',
                      }}>
                      <Radio
                        checked={previsao === 50}
                        onChange={handlePrevisao}
                        value="50"
                        name="radio-button-demo"
                        inputProps={{ 'aria-label': 50 }}
                      />
                      <Typography variant="caption">50 {t('forms.euscoast.years')}</Typography>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        textAlign: 'center',
                        alignItems: 'center',
                      }}>
                      <Radio
                        checked={previsao === 100}
                        onChange={handlePrevisao}
                        value="100"
                        name="radio-button-demo"
                        inputProps={{ 'aria-label': 100 }}
                      />
                      <Typography variant="caption">100 {t('forms.euscoast.years')}</Typography>
                    </div>
                  </Box>
                </Card>
                <Card style={{ padding: 15 }} elevation={3}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="center"
                    marginBottom={2}
                    style={{ gap: 10 }}>
                    <Typography variant="h6">{t('forms.euscoast.result.transect')}</Typography>
                    <TextField
                      type="number"
                      id="outlined-basic"
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 1, max: transectsData.length }}
                      onChange={(e) => setSelectedTransect(e.target.value)}
                      value={selectedTransect}
                      style={{ width: 80, textAlign: 'center' }}
                    />
                  </Box>
                  <TransectResult data={[...(transectsCalcs[selectedTransect] ?? [])]} />
                </Card>
                <Card style={{ padding: 15 }} elevation={3}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Button
                      color="secondary"
                      variant="contained"
                      style={{
                        width: '100%',
                        height: 35,
                        padding: 'inherit 20px',
                        marginBottom: 10,
                      }}
                      onClick={() =>
                        exportToCsv(
                          `cassie_euscoast_${years.map(periodLabel).map(periodFilenamePart).join('-')}_results_${new Date().toLocaleDateString()}`,
                          transectsCsv,
                        )
                      }>
                      {t('forms.euscoast.result.export').toUpperCase()}
                    </Button>
                    <Tooltip title={t('forms.euscoast.result.export')}>
                      <IconButton onClick={() => setShowHelpDialogCSV(true)} size="small" style={{ marginLeft: 8 }}>
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <SimpleInfoDialog
                    open={showHelpDialogCSV}
                    close={() => setShowHelpDialogCSV(false)}
                    title="forms.euscoast.result.export"
                    text="forms.euscoast.result.exportInfo"
                  />
                  <Box display="flex" alignItems="center" mb={1}>
                    <Button
                      color="secondary"
                      variant="contained"
                      style={{
                        width: '100%',
                        height: 35,
                        padding: 'inherit 20px',
                      }}
                      onClick={handleShapefileExport}>
                      {t('forms.euscoast.result.shp').toUpperCase()}
                    </Button>
                    <Tooltip title={t('forms.euscoast.result.shp')}>
                      <IconButton onClick={() => setShowHelpDialogShp(true)} size="small" style={{ marginLeft: 8 }}>
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <SimpleInfoDialog
                    open={showHelpDialogShp}
                    close={() => setShowHelpDialogShp(false)}
                    title="forms.euscoast.result.shp"
                    text="forms.euscoast.result.shpInfo"
                  />
                  <Box display="flex" alignItems="center">
                    <Button
                      color="secondary"
                      variant="contained"
                      disabled={Object.keys(classLandCoverPolygons).length === 0}
                      style={{
                        width: '100%',
                        height: 35,
                        padding: 'inherit 20px',
                      }}
                      onClick={handleVectorExport}>
                      {t('forms.euscoast.result.vector').toUpperCase()}
                    </Button>
                    <Tooltip title={t('forms.euscoast.result.vector')}>
                      <IconButton onClick={() => setShowHelpDialogVector(true)} size="small" style={{ marginLeft: 8 }}>
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <SimpleInfoDialog
                    open={showHelpDialogVector}
                    close={() => setShowHelpDialogVector(false)}
                    title="forms.euscoast.result.vector"
                    text="forms.euscoast.result.vectorInfo"
                  />
                </Card>
              </Box>
            )}
            {analyzing && (
              <Card style={{ padding: 15, width: '100%' }} elevation={3} className={classes.card}>
                <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: 10 }}>
                  <Typography align="center">{calcMessage}</Typography>
                  <Typography variant="caption" align="center">
                    {t('forms.euscoast.result.loading.wait')}
                  </Typography>
                </Box>
              </Card>
            )}
          </Grid>
          <Grid className={classes.rightContainer}>
            {showLegendCard && (
              <Card style={{ padding: 15 }} elevation={3} className={classes.card}>
                <Typography fontSize={20} style={{ fontWeight: 'bold', marginBottom: 10 }}>
                  {t('forms.euscoast.result.colorLegend')} {t('forms.euscoast.result.transects').toLowerCase()}
                </Typography>
                <Box
                  display="grid"
                  gridTemplateColumns="auto 1fr"
                  alignItems="center"
                  style={{ gap: '10px', rowGap: '5px' }}>
                  <Box width={30} height={5} style={{ backgroundColor: 'red' }} borderRadius={5}></Box>
                  <Typography variant="body2">{t('forms.euscoast.result.legend.advancedGrowth')}</Typography>
                  <Box width={30} height={5} style={{ backgroundColor: 'yellow' }} borderRadius={5}></Box>
                  <Typography variant="body2">{t('forms.euscoast.result.legend.moderateGrowth')}</Typography>
                  <Box width={30} height={5} style={{ backgroundColor: 'green' }} borderRadius={5}></Box>
                  <Typography variant="body2">{t('forms.euscoast.result.legend.stable')}</Typography>
                  <Box width={30} height={5} style={{ backgroundColor: 'gray' }} borderRadius={5}></Box>
                  <Typography variant="body2">{t('forms.euscoast.result.legend.noUrban')}</Typography>
                </Box>
              </Card>
            )}
            <Card
              style={{ padding: 15, height: 'fit-content', overflow: 'unset' }}
              elevation={3}
              className={classes.card}>
              <div className={classes.windowHeader}>
                <Box display="flex" alignItems="center">
                  <Typography
                    style={{
                      fontWeight: 'bold',
                      paddingBottom: 5,
                      marginBottom: 5,
                      marginRight: 8,
                    }}>
                    {t('forms.euscoast.result.visibilityController')}
                  </Typography>
                  <Tooltip title={t('forms.euscoast.result.visibilityController')}>
                    <IconButton onClick={() => setShowHelpDialogVisibility(true)} size="small" style={{ padding: 4 }}>
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <SimpleInfoDialog
                  open={showHelpDialogVisibility}
                  close={() => setShowHelpDialogVisibility(false)}
                  title="forms.euscoast.result.visibilityController"
                  text="forms.euscoast.result.visibilityControllerinfo"
                />
              </div>
              <Typography variant="caption">{t('forms.euscoast.result.visibilityControllerDesc')}</Typography>
            </Card>
            {Object.values(images)
              .reverse()
              .map((image, index) => (
                <Card
                  style={{
                    padding: 12,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                  key={index}
                  elevation={3}>
                  <div
                    style={{
                      fontWeight: 'bold',
                      borderBottom: '1px solid gray',
                      paddingBottom: 5,
                      marginBottom: 5,
                    }}>
                    {periodLabel(image.name)} ({image.missionName})
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      padding: '10px 0',
                    }}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '90px 1fr auto auto',
                        gap: 10,
                      }}>
                      <div>Raster</div>
                      <Slider
                        key={opacity[index].raster > 0 ? 1 : 0}
                        value={opacity[index].raster}
                        step={0.05}
                        min={0.0}
                        max={1.0}
                        onChange={(_, value) => {
                          let level = opacityChange(value, image.classification);
                          setOpacity({
                            ...opacity,
                            [index]: { ...opacity[index], raster: level },
                          });
                        }}
                        aria-labelledby="continuous-slider"
                      />
                      <IconButton
                        onClick={() => {
                          let level = opacityChange(image.classification.opacity ? 0 : 1, image.classification);
                          setOpacity({
                            ...opacity,
                            [index]: { ...opacity[index], raster: level },
                          });
                        }}
                        style={{
                          marginLeft: 14,
                          width: 10,
                          height: 10,
                        }}>
                        {opacity[index].raster > 0 ? (
                          <VisibilityIcon fontSize="large" color="primary" />
                        ) : (
                          <VisibilityOffIcon fontSize="large" />
                        )}
                      </IconButton>
                      <IconButton
                        onClick={() => window.open(image.classification.url, 'download')}
                        style={{
                          width: 10,
                          height: 10,
                        }}>
                        <GetAppIcon fontSize="large" />
                      </IconButton>
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '90px 1fr auto auto',
                        gap: 10,
                      }}>
                      <div>Satellite image</div>
                      <Slider
                        key={opacity[index].overlay > 0 ? 1 : 0}
                        value={opacity[index].overlay}
                        step={0.05}
                        min={0.0}
                        max={1.0}
                        onChange={(_, value) => {
                          let level = opacityChange(value, image.overlay);
                          setOpacity({
                            ...opacity,
                            [index]: { ...opacity[index], overlay: level },
                          });
                        }}
                        aria-labelledby="non-linear-slider"
                      />
                      <IconButton
                        onClick={() => {
                          let level = opacityChange(image.overlay.opacity ? 0 : 1, image.overlay);
                          setOpacity({
                            ...opacity,
                            [index]: { ...opacity[index], overlay: level },
                          });
                        }}
                        style={{
                          marginLeft: 14,
                          width: 10,
                          height: 10,
                        }}>
                        {opacity[index].overlay > 0 ? (
                          <VisibilityIcon fontSize="large" color="primary" />
                        ) : (
                          <VisibilityOffIcon fontSize="large" />
                        )}
                      </IconButton>
                      <IconButton
                        onClick={() => window.open(image.url, 'download')}
                        style={{
                          width: 10,
                          height: 10,
                        }}>
                        <GetAppIcon fontSize="large" />
                      </IconButton>
                    </div>
                  </div>
                </Card>
              ))}
            {(analyzing || finished) && (
              <Card style={{ padding: 15, height: 'fit-content' }} elevation={3} className={classes.card}>
                <div
                  style={{
                    fontWeight: 'bold',
                    borderBottom: '1px solid gray',
                    paddingBottom: 5,
                    marginBottom: 5,
                  }}>
                  {t('forms.euscoast.result.polygons')}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    padding: '10px 0',
                  }}>
                  {transectsObjects.length > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 10,
                      }}>
                      <div>{t('forms.euscoast.result.transects')}</div>
                      <IconButton
                        onClick={() => {
                          let level = opacityTransectsChange();
                          setOpacity({
                            ...opacity,
                            2: { ...opacity[2], transects: level },
                          });
                        }}
                        style={{
                          marginLeft: 14,
                          width: 10,
                          height: 10,
                        }}>
                        {opacity[2].transects ? (
                          <VisibilityIcon fontSize="large" color="primary" />
                        ) : (
                          <VisibilityOffIcon fontSize="large" />
                        )}
                      </IconButton>
                    </div>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 10,
                    }}>
                    <div>{t('forms.euscoast.result.baseLine')}</div>
                    <IconButton
                      onClick={() => {
                        let level = opacityBaseLineChange();
                        setOpacity({
                          ...opacity,
                          2: { ...opacity[2], baseline: level },
                        });
                      }}
                      style={{
                        marginLeft: 14,
                        width: 10,
                        height: 10,
                      }}>
                      {opacity[2].baseline ? (
                        <VisibilityIcon fontSize="large" color="primary" />
                      ) : (
                        <VisibilityOffIcon fontSize="large" />
                      )}
                    </IconButton>
                  </div>
                </div>
              </Card>
            )}
          </Grid>
        </Grid>
        <ReturnButtons lastStepPath="/main/euscoast/5" buttonClassName={classes.returnButton} />
        <GoogleMap
          onLoad={(map) => {
            mapRef.current = map;
            setMapReady(true);
            displayROI(map);
            transectMarker.current = new window.google.maps.Marker({
              map: map,
              visible: false,
              label: '',
            });

          }}
          onBoundsChanged={(bounds) => {
            boundsRef.current = bounds;
          }}
          style={{ position: 'absolute' }}
          mapTypeControlOpt={{
            style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: window.google.maps.ControlPosition.BOTTOM_CENTER,
            mapTypeIds: [
              window.google.maps.MapTypeId.ROADMAP,
              window.google.maps.MapTypeId.SATELLITE,
              window.google.maps.MapTypeId.HYBRID,
            ],
          }}
        />
      </Box>
    </ThemeProvider>
  );
};

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles for the
 * EUSCoast processing page.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Euscoast
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    maxHeight: '100vh',
    flexGrow: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  mapContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    flexFlow: 'column',
    paddingTop: OVERLAY_COLUMN_PADDING_TOP,
    paddingBottom: 10,
    paddingLeft: OVERLAY_SIDE_GUTTER,
    paddingRight: OVERLAY_SCROLLBAR_GUTTER,
    gap: 10,
    overflowY: 'auto',
    maxHeight: OVERLAY_COLUMN_MAX_HEIGHT,
    ...OVERLAY_COLUMN_LAYOUT,
    scrollbarWidth: 'thin',
    scrollbarColor: 'transparent transparent',
    transition: 'scrollbar-color 0.3s ease',
    '&:hover': {
      scrollbarColor: '#c1c1c1 transparent',
    },
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'transparent',
      borderRadius: '10px',
      transition: 'background-color 0.3s ease',
    },
    '&:hover::-webkit-scrollbar-thumb': {
      backgroundColor: '#c1c1c1',
    },
  },
  rightContainer: {
    display: 'grid',
    justifySelf: 'end',
    width: '100%',
    paddingTop: OVERLAY_COLUMN_PADDING_TOP,
    paddingLeft: OVERLAY_SCROLLBAR_GUTTER,
    paddingRight: OVERLAY_SIDE_GUTTER,
    gap: '10px',
    alignContent: 'start',
    overflowY: 'auto',
    maxHeight: OVERLAY_COLUMN_MAX_HEIGHT,
    zIndex: 10,
    ...OVERLAY_COLUMN_LAYOUT,
    scrollbarWidth: 'thin',
    scrollbarColor: 'transparent transparent',
    transition: 'scrollbar-color 0.3s ease',
    '&:hover': {
      scrollbarColor: '#c1c1c1 transparent',
    },
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'transparent',
      borderRadius: '10px',
      transition: 'background-color 0.3s ease',
    },
    '&:hover::-webkit-scrollbar-thumb': {
      backgroundColor: '#c1c1c1',
    },
  },
  mapOverlay: {
    display: 'grid',
    width: '100%',
    position: 'absolute',
    top: 0,
    bottom: 0,
    zIndex: 10,
    gridTemplateColumns: 'minmax(0, 430px) minmax(0, 280px)',
    gridTemplateRows: '100%',
    pointerEvents: 'none',
    '& > *': {
      pointerEvents: 'none',
      '& > *': {
        pointerEvents: 'auto',
      },
    },
  },
  card: {
    height: 'fit-content !important',
    overflow: 'unset !important',
    '& .MuiCard-root': {
      overflow: 'unset !important',
    },
  },
  windowHeader: {
    paddingBottom: 5,
  },
  returnButton: {
    width: '100%',
  },
}));

export default ProcessingPage;
